import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  Bot,
  Search,
  Sparkles,
  Menu,
  Sun,
  Moon,
  ShieldAlert,
  Activity,
  TrendingUp,
  FileSpreadsheet,
  ChevronLeft,
  FileWarning, X, HelpCircle} from "lucide-react";
import { AllMethodologiesResult, HistoryItem, AppUser } from "./types";
import { parseRiskScore } from "./utils/math_helpers";
import * as api from "./utils/api";

import { LogoWordmark } from "./components/Logo";
import { useIsMobile } from "./utils/viewport";
import { onSwipe } from "./utils/gestures";
import Sidebar from "./components/Sidebar";
import HelpDrawer from "./components/HelpDrawer";
import UpdateBanner from "./components/UpdateBanner";
import InviteSetPassword from "./components/InviteSetPassword";
import AuthPanel from "./components/AuthPanel";
import AnalysisSearchForm from "./components/AnalysisSearchForm";
import FullPageSection from "./components/FullPageSection";
import { useAuthForm } from "./hooks/useAuthForm";
import { useNotifications } from "./hooks/useNotifications";
import { openReportWindow } from "./utils/reportWindow";
import AiAgent from "./components/AiAgent";
import ResultTabs, { ResultTab } from "./components/ResultTabs";
const BankRegistry = lazy(() => import("./components/BankRegistry"));
// Кабинет площадки тянет за собой общую форму условий (PlatformForm) — тяжёлый кусок,
// нужный меньшинству пользователей. Статический импорт выносил его в главный бандл и
// пробивал бюджет entry-чанка (#592).
const PlatformCabinet = lazy(() => import("./components/PlatformCabinet"));
const CreditProducts = lazy(() => import("./components/CreditProducts"));
const ProductCompare = lazy(() => import("./components/ProductCompare"));
const BrokerAnalytics = lazy(() => import("./components/BrokerAnalytics"));
const ClientExperience = lazy(() => import("./components/ClientExperience"));
const ClientsTab = lazy(() => import("./components/ClientsTab"));
const ApplicationsTab = lazy(() => import("./components/ApplicationsTab"));
const HistorySidebar = lazy(() => import("./components/HistorySidebar"));
const CabinetTab = lazy(() => import("./components/CabinetTab"));
const OrganizationTab = lazy(() => import("./components/OrganizationTab"));
const TenderAnalyticsLaunch = lazy(() => import("./components/TenderAnalyticsLaunch"));
const AdminTab = lazy(() => import("./components/AdminTab"));
const AdminDocs = lazy(() => import("./components/AdminDocs"));
const HelpAdmin = lazy(() => import("./components/HelpAdmin"));
const HelpCenter = lazy(() => import("./components/HelpCenter"));
import type { InfographicKind } from "./components/InfographicModal";
const InfographicModal = lazy(() => import("./components/InfographicModal"));
const SbExplanationModal = lazy(() => import("./components/SbExplanationModal"));
const MethodologyBuilder = lazy(() => import("./components/MethodologyBuilder"));
const BankMethods = lazy(() => import("./components/BankMethods"));
const BankMethodologyPicker = lazy(() => import("./components/BankMethodologyPicker"));
const CompanyProfileCard = lazy(() => import("./components/CompanyProfileCard"));
import { PremiumPromo, PremiumModal } from "./components/Premium";
const ProjectPromo = lazy(() => import("./components/ProjectPromo"));
const QuarterlyReports = lazy(() => import("./components/QuarterlyReports"));

// Цвет тоста уведомления по заявке СНС: зелёный — хорошая новость, жёлтый — нужно действие, красный — отказ.
const TOAST_TONE: Record<string, string> = {
  positive: "bg-emerald-600", attention: "bg-amber-600", negative: "bg-rose-600", neutral: "bg-indigo-600",
};

// В ключе версия: у вкладок, успевших сохранить раздел по прежнему правилу, там лежит
// «Анализ» — он попал туда при выходе из системы. Смена ключа гасит это, иначе такая
// вкладка продолжала бы открывать «Анализ» и после исправления (#647).
const VIEW_KEY = "anyskin:view:2";

/** Раздел, в котором брокер был до обновления страницы (#647).
 *
 *  Восстанавливаем только рабочие разделы: админские зависят от прав, которые могли
 *  измениться, а «Справка» и «О проекте» — это не место, куда возвращаются по F5. */
const RESTORABLE = new Set(["main", "broker", "agent", "platform", "clients", "applications",
  "banks", "credit", "tenders", "reports", "builder", "bankmethods", "product_compare",
  "experience", "org", "cabinet"]);

function restoreView(): any {
  try {
    const v = window.sessionStorage.getItem(VIEW_KEY);
    return v && RESTORABLE.has(v) ? v : null;
  } catch { return null; }   // приватный режим — просто стартуем со «Скоринга»
}

/** Раздел, с которого начинают работу: «Скоринг», на мобильном — «AI агент».
 *
 *  Одно место на всех: и старт приложения, и возврат после выхода. Пока выход вёл в
 *  «Анализ» отдельной строкой, сохранённым разделом становился он — и дальше каждый
 *  вход в этой вкладке открывал «Анализ», мимо нового стартового раздела (#647). */
function startView(): any {
  return typeof window !== "undefined" && window.innerWidth < 768 ? "agent" : "broker";
}

export default function App() {
  const [inn, setInn] = useState("");
  const [desiredLimit, setDesiredLimit] = useState<string>("10000000");
  // Загруженная пользователем отчётность для расчёта «по своим данным».
  const [reportFile, setReportFile] = useState<File | null>(null);
  // true, пока идёт расчёт по скану (PDF без текста/изображение) — OCR долгий,
  // показываем отдельную подсказку в индикаторе загрузки.
  const [ocrPending, setOcrPending] = useState(false);
  const reportInputRef = useRef<HTMLInputElement | null>(null);
  // Сохранённый фактический результат на время проекции по целевому балансу.
  const projectionBackup = useRef<AllMethodologiesResult | null>(null);
  const [activeTab, setActiveTab] = useState<ResultTab>("compare");
  // id выбранной пользовательской методики (когда activeTab === "custom").
  const [activeCustomId, setActiveCustomId] = useState<number | null>(null);
  // открыт ли выбор банковских методик (премиум-пикер).
  const [showBankPicker, setShowBankPicker] = useState(false);
  // открыто ли боковое меню в мобильной версии (off-canvas drawer).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Свайп вправо по левой половине экрана открывает меню (P3-02); закрывает — свайп влево.
  const isPhone = useIsMobile();
  // тёмная тема (класс .dark на <html>, начальное значение проставляет скрипт в index.html).
  const [dark, setDark] = useState(() => typeof document !== "undefined" && document.documentElement.classList.contains("dark"));
  const toggleTheme = () => {
    const next = !dark;
    document.documentElement.classList.toggle("dark", next);
    try { localStorage.setItem("anyskin.theme", next ? "dark" : "light"); } catch { /* noop */ }
    setDark(next);
  };
  // Глобальный режим страницы. «admin» и «cabinet» — отдельные полностраничные
  // экраны; вкладки методик / форма расчёта в них не рендерятся. Переключатели —
  // плашки в шапке (user pill и admin pill).
  // Стартовый раздел — «Скоринг»: с него начинается работа брокера, а «Анализ» это
  // отдельный инструмент, в который не надо попадать по умолчанию (#647).
  // На мобильной версии — «AI агент» (ширина < md = 768px).

  const [view, setView] = useState<"main" | "admin" | "admindocs" | "cabinet" | "org" | "builder" | "reports" | "clients" | "agent" | "banks" | "credit" | "bankmethods" | "broker" | "platform" | "experience" | "premium" | "about" | "tenders" | "product_compare" | "help_admin" | "help" | "applications">(
    () => restoreView() || startView());

  // F5 не должен выбрасывать из раздела: брокер обновляет страницу, когда заявка висит
  // в обработке, и оказывался в «Анализе» — вместе с потерей места в работе (#647).
  // Сессия, не localStorage: новый вход начинается со «Скоринга», обновление — там же,
  // где были. Админские разделы не восстанавливаем — права могли измениться.
  useEffect(() => {
    try { window.sessionStorage.setItem(VIEW_KEY, view); } catch { /* приватный режим */ }
  }, [view]);
  // Подбор из карточки клиента на повторный пересчёт в разделе «Брокер» (#442).
  const [brokerRepeat, setBrokerRepeat] = useState<api.BrokerSelection | null>(null);
  // ИНН клиента, с которым открыли «Скоринг» из его карточки: набирать заново незачем.
  const [brokerInn, setBrokerInn] = useState<string | null>(null);
  const [result, setResult] = useState<AllMethodologiesResult | null>(null);
  // Вопрос из «Подробнее» (рекомендации/термины методик) — передаём в ИИ-агент и
  // открываем его сразу с готовым вопросом вместо статичной подсказки/цитаты.
  const [agentPrefill, setAgentPrefill] = useState<string | null>(null);
  // На компьютере — плавающее окно в правом нижнем углу (как «Диалог по заявке» в
  // Брокере, BrokerChat), текущий экран не теряется. На телефоне — тому плавающему
  // окну негде поместиться толком, открываем прежний полноэкранный агент.
  const [agentFloatingOpen, setAgentFloatingOpen] = useState(false);
  // Контекст, с которым открыли ассистента (A16, #1189): заявка, о которой спрашивают.
  // Держится до закрытия окна, а не до первого ответа: весь разговор об одной заявке.
  const [agentContext, setAgentContext] = useState<{ context: Record<string, any> | null; product?: string } | null>(null);
  const askAssistant = (question: string) => {
    setAgentContext(null);
    setAgentPrefill(question);
    if (isPhone) setView("agent"); else setAgentFloatingOpen(true);
  };
  // Перетаскивание/растягивание плавающего окна ассистента — тот же механизм, что
  // у «Диалога по заявке» в Брокере (BrokerChat.tsx): первое взаимодействие
  // фиксирует текущий прямоугольник, дальше двигаем/тянем по мыши.
  const agentPanelRef = useRef<HTMLDivElement | null>(null);
  const [agentBox, setAgentBox] = useState<{ left: number; top: number; width: number; height: number } | null>(null);
  const [agentDragging, setAgentDragging] = useState(false);
  const [agentResizing, setAgentResizing] = useState(false);
  const agentDragRef = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null);
  const agentRzRef = useRef<{ sx: number; sy: number; w: number; h: number; left: number; top: number; dir: string } | null>(null);
  const agentRafRef = useRef<number | null>(null);
  const agentPtrRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const beginAgentBox = () => {
    if (agentBox) return agentBox;
    const r = agentPanelRef.current?.getBoundingClientRect();
    const b = r ? { left: r.left, top: r.top, width: r.width, height: r.height } : { left: 60, top: 90, width: 420, height: 640 };
    setAgentBox(b); return b;
  };
  const startAgentDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const b = beginAgentBox();
    agentDragRef.current = { sx: e.clientX, sy: e.clientY, bx: b.left, by: b.top };
    setAgentDragging(true);
  };
  const startAgentResize = (e: React.MouseEvent, dir: string) => {
    e.stopPropagation(); e.preventDefault();
    const b = beginAgentBox();
    agentRzRef.current = { sx: e.clientX, sy: e.clientY, w: b.width, h: b.height, left: b.left, top: b.top, dir };
    setAgentResizing(true);
  };
  useEffect(() => {
    if (!agentDragging) return;
    const apply = () => {
      agentRafRef.current = null;
      const d = agentDragRef.current; if (!d) return;
      setAgentBox((b) => b && {
        ...b,
        left: Math.max(0, Math.min(window.innerWidth - 120, d.bx + (agentPtrRef.current.x - d.sx))),
        top: Math.max(0, Math.min(window.innerHeight - 44, d.by + (agentPtrRef.current.y - d.sy))),
      });
    };
    const move = (e: MouseEvent) => {
      agentPtrRef.current = { x: e.clientX, y: e.clientY };
      if (agentRafRef.current == null) agentRafRef.current = requestAnimationFrame(apply);
    };
    const up = () => setAgentDragging(false);
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      if (agentRafRef.current != null) { cancelAnimationFrame(agentRafRef.current); agentRafRef.current = null; }
    };
  }, [agentDragging]);
  useEffect(() => {
    if (!agentResizing) return;
    const apply = () => {
      agentRafRef.current = null;
      const d = agentRzRef.current; if (!d) return;
      const dx = agentPtrRef.current.x - d.sx, dy = agentPtrRef.current.y - d.sy;
      const MINW = 320, MINH = 320;
      setAgentBox((b) => {
        if (!b) return b;
        let left = d.left, top = d.top, width = d.w, height = d.h;
        if (d.dir.includes("e")) width = Math.max(MINW, d.w + dx);
        if (d.dir.includes("s")) height = Math.max(MINH, d.h + dy);
        if (d.dir.includes("w")) { width = Math.max(MINW, d.w - dx); left = d.left + (d.w - width); }
        if (d.dir.includes("n")) { height = Math.max(MINH, d.h - dy); top = d.top + (d.h - height); }
        left = Math.max(0, left); top = Math.max(0, top);
        width = Math.min(width, window.innerWidth - left);
        height = Math.min(height, window.innerHeight - top);
        return { left, top, width, height };
      });
    };
    const move = (e: MouseEvent) => {
      agentPtrRef.current = { x: e.clientX, y: e.clientY };
      if (agentRafRef.current == null) agentRafRef.current = requestAnimationFrame(apply);
    };
    const up = () => setAgentResizing(false);
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      if (agentRafRef.current != null) { cancelAnimationFrame(agentRafRef.current); agentRafRef.current = null; }
    };
  }, [agentResizing]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Апселл-модалка (лимит ИНН / клик по премиум-функции). Текст или null.
  const [premiumModal, setPremiumModal] = useState<string | null>(null);
  
  // History tracking
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // User auth state — приводим к нашему /api/auth/me-формату.
  const [currentUser, setCurrentUser] = useState<AppUser | null>(api.getUser());
  useEffect(() => {
    if (!isPhone || mobileNavOpen || !currentUser) return;
    return onSwipe(document.documentElement, "right", () => setMobileNavOpen(true));
  }, [isPhone, mobileNavOpen, currentUser]);
  // Задачи подбора (брокер) и непрочитанные события по заявкам СНС — опрос,
  // бейджи в меню, всплывающий тост. Состояние и polling-эффекты — в хуке.
  const notif = useNotifications(currentUser);
  // «Перейти в Заявки» из окна подачи / диалога.
  useEffect(() => {
    const h = (e: Event) => { notif.setAppRef((e as CustomEvent).detail?.ref || null); setView("applications"); };
    window.addEventListener("anyskin:open-application", h);
    return () => window.removeEventListener("anyskin:open-application", h);
  }, []);
  // «Спросить про эту заявку» (A16, #1189): вопрос и контекст заявки едут в ассистента
  // вместе. На телефоне он открывается разделом, на большом экране — плавающим окном
  // рядом с карточкой: уводить с экрана заявки, о которой спрашивают, незачем.
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      setAgentContext({ context: d.context || null, product: d.product || undefined });
      setAgentPrefill(d.question || null);
      if (isPhone) setView("agent"); else setAgentFloatingOpen(true);
    };
    window.addEventListener("anyskin:ask-assistant", h);
    return () => window.removeEventListener("anyskin:ask-assistant", h);
  }, [isPhone]);
  useEffect(() => { document.documentElement.dataset.view = view; }, [view]);   // раздел — в отчёты об ошибках (M-71)
  const isPremium = !!(currentUser?.is_premium || currentUser?.is_admin);
  // Ошибки расчёта: лимит бесплатного доступа → модалка апселла, иначе — текст.
  const onCalcError = (err: any, fallback: string) => {
    if (err?.code === "inn_limit") setPremiumModal(err.message || "Достигнут дневной лимит запросов по ИНН.");
    else setError(err?.message || fallback);
  };
  // Дашборд (2 колонки с результатом) vs «поисковая» главная по центру (при входе,
  // пока нет результата/загрузки/ошибки) — без пустой правой панели ожидания.
  const dashboard = !!result || isLoading || !!error;
  // Ссылка из письма-приглашения (?invite=token) — полноэкранная форма установки
  // пароля вместо обычного входа (сотрудник ещё не может войти обычным паролем).
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  // Состояние и обработчики формы входа/регистрации — вынесены в хук (useAuthForm.ts),
  // JSX карточки — в components/AuthPanel.tsx.
  const auth = useAuthForm(currentUser, setCurrentUser, () => { setActiveTab("compare"); fetchHistory(); });
  // Modal infographic details state
  // Тип субъекта приходит из реестра: у предпринимателя другой набор реквизитов,
  // а часть блоков (учредители, уставный капитал) не существует в принципе.
  const isIp = (result?.company as { kind?: string } | undefined)?.kind === "ip";
  const [selectedInfographic, setSelectedInfographic] = useState<InfographicKind | null>(null);
  const [showSbExplanation, setShowSbExplanation] = useState(false);
  // Реф на правую колонку с результатами — для авто-скролла к расчёту на мобиле.
  const resultsRef = useRef<HTMLElement>(null);

  // riskCheckScore приходит от бэка (sb_score.compute_sb_reliability) в формате
  // "X.X/10 (Метка)". Парсер живёт в utils/math_helpers — переиспользуем
  // в App-плашке и в SbExplanationModal.
  const sb = parseRiskScore(result?.company?.riskCheckScore);

  const fetchHistory = async () => {
    if (!api.getToken()) {
      setHistory([]);
      return;
    }
    setIsLoadingHistory(true);
    try {
      const list = await api.fetchHistory(50);
      setHistory(list as HistoryItem[]);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // На каждом ?confirm=ok|invalid показываем баннер и убираем query.
  // На монтировании синхронизируем сессию через /api/auth/me — если токен
  // протух, очищаем localStorage и сидим на форме входа.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("confirm");
    if (c === "ok") {
      auth.setAuthBanner({ text: "Email подтверждён. Войдите под своим логином.", kind: "ok" });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (c === "invalid") {
      auth.setAuthBanner({ text: "Ссылка недействительна или истекла. Запросите письмо повторно.", kind: "error" });
      window.history.replaceState({}, "", window.location.pathname);
    }
    const inv = params.get("invite");
    if (inv) {
      setInviteToken(inv);
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (api.getToken()) {
      api.whoami()
        .then(me => {
          api.setSession(api.getToken() as string, me);
          setCurrentUser(me);
          fetchHistory();
        })
        .catch(() => {
          api.clearSession();
          setCurrentUser(null);
        });
    }
    // Слушаем смену сессии в этой вкладке.
    const onAuth = () => setCurrentUser(api.getUser());
    window.addEventListener("anyskin-auth", onAuth);
    return () => window.removeEventListener("anyskin-auth", onAuth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Технические колонки «Формула / Расчёт / Диапазоны» по-прежнему доступны
  // только админам — управляется глобальным CSS-классом body.hide-col-*.
  // Для не-админов классы принудительно устанавливаем; админы видят колонки всегда.
  useEffect(() => {
    const classes = ["hide-col-formula", "hide-col-calc", "hide-col-ranges"];
    if (currentUser && !currentUser.is_admin) {
      classes.forEach(c => document.body.classList.add(c));
    } else {
      classes.forEach(c => document.body.classList.remove(c));
    }
  }, [currentUser]);

  // На мобиле форма и история стоят выше результатов (естественный порядок).
  // При старте расчёта и при получении данных подматываем правую колонку в зону
  // видимости — чтобы был виден индикатор загрузки, а затем карточка компании.
  // На десктопе колонки идут бок о бок, скролл не нужен.
  useEffect(() => {
    if (!resultsRef.current) return;
    if (!window.matchMedia("(max-width: 1023px)").matches) return;
    if (isLoading || result) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isLoading, result]);

  // ИНН-тип: 10 → юрлицо, 12 → индивидуальный предприниматель. Физлицо без статуса ИП
  // отсекает бэкенд после обращения к реестру — по длине ИНН их не различить.
  const innType: "ul" | "ip" | "invalid" = (() => {
    if (!/^\d+$/.test(inn)) return "invalid";
    if (inn.length === 10) return "ul";
    if (inn.length === 12) return "ip";
    return "invalid";
  })();

  const handleCalculate = async (targetInn: string) => {
    if (!targetInn) return;
    // Предприниматель считается только по приложенной отчётности — направляем туда же,
    // куда ведёт кнопка «по вашей отчётности», чтобы не было двух разных сценариев.
    if (targetInn.length === 12) {
      if (!reportFile) {
        setError("Приложите годовую отчётность — без неё расчёт по предпринимателю не выполняется.");
        return;
      }
      await handleCalculateWithReport();
      return;
    }
    if (!/^\d{10}$|^\d{12}$/.test(targetInn)) {
      setError("Введите корректный ИНН (10 или 12 цифр)");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.calculateAll(targetInn, desiredLimit ? Number(desiredLimit) : 10_000_000);
      setResult(data);
      await fetchHistory();
    } catch (err: any) {
      onCalcError(err, "Не удалось получить результаты расчёта.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalculateWithReport = async () => {
    if (!inn.trim() || !reportFile) return;
    if (!/^\d{10}$|^\d{12}$/.test(inn)) { setError("Введите корректный ИНН (10 или 12 цифр)"); return; }
    setOcrPending(/\.(pdf|png|jpe?g|tiff?|webp)$/i.test(reportFile.name));
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.calculateWithReport(
        inn, desiredLimit ? Number(desiredLimit) : 10_000_000, reportFile);
      setResult(data);
    } catch (err: any) {
      onCalcError(err, "Не удалось рассчитать по загруженной отчётности.");
    } finally {
      setIsLoading(false);
      setOcrPending(false);
    }
  };

  // Проекция: пересчитать все методики по рекомендованному (целевому) балансу.
  const handleRecalcTarget = async () => {
    if (!result?.targetReport || !result.company?.inn) return;
    const balance: Record<string, number> = {};
    result.targetReport.lines.forEach((l) => { balance[l.code] = l.target; });
    if (!result.projected) projectionBackup.current = result;  // сохранить факт
    setIsLoading(true);
    setError(null);
    try {
      const proj = await api.calculateWithBalance(
        result.company.inn, desiredLimit ? Number(desiredLimit) : 10_000_000, balance);
      setResult(proj);
      setActiveTab("compare");
    } catch (err: any) {
      onCalcError(err, "Не удалось пересчитать по целевому балансу.");
    } finally {
      setIsLoading(false);
    }
  };
  // Та же проекция, но по целевому балансу ИМЕННО по Крылову (result.krylov.targetReport,
  // не общий result.targetReport) — остаётся на вкладке «Углублённый анализ».
  const handleRecalcKrylovTarget = async () => {
    const tr = result?.krylov?.targetReport;
    if (!tr || !result?.company?.inn) return;
    const balance: Record<string, number> = {};
    tr.lines.forEach((l) => { balance[l.code] = l.target; });
    if (!result.projected) projectionBackup.current = result;
    setIsLoading(true);
    setError(null);
    try {
      const proj = await api.calculateWithBalance(
        result.company.inn, desiredLimit ? Number(desiredLimit) : 10_000_000, balance);
      setResult(proj);
      setActiveTab("krylov");
    } catch (err: any) {
      onCalcError(err, "Не удалось пересчитать по целевому балансу (Крылов).");
    } finally {
      setIsLoading(false);
    }
  };
  const exitProjection = () => {
    if (projectionBackup.current) { setResult(projectionBackup.current); projectionBackup.current = null; }
  };

  // Полный PDF-отчёт: сперва АВТО-догружаем связи руководителя/учредителя (в UI —
  // по кнопке; в отчёт кладём все), затем открываем печатную версию.
  const [reportBusy, setReportBusy] = useState(false);
  const handleFullReport = async () => {
    if (!result) return;
    // Окно ОБЯЗАТЕЛЬНО открываем синхронно (в жесте клика), иначе попап-блокер → about:blank.
    const win = openReportWindow();
    let full = result;
    const pending = result.related?.pending || [];
    if (pending.length) {
      setReportBusy(true);
      try {
        const r = await api.resolveRelated(pending);
        full = { ...result, related: { ...(result.related as any),
          companies: [...(result.related?.companies || []), ...(r.companies || [])], pending: [] } };
      } catch { /* всё равно откроем отчёт с тем, что уже есть */ }
      finally { setReportBusy(false); }
    }
    const { openReport } = await import("./utils/reportHtml");
    openReport("compare", full, win);
  };


  const handleLogout = () => {
    api.clearSession();
    setCurrentUser(null);
    setResult(null);
    setHistory([]);
    setActiveTab("compare");
    setView(startView());
    // Раздел предыдущего пользователя не должен открыться у следующего: и как чужой
    // контекст на экране, и как подсказка, чем тут занимались.
    try { window.sessionStorage.removeItem(VIEW_KEY); } catch { /* приватный режим */ }
    document.body.classList.remove("hide-col-formula", "hide-col-calc", "hide-col-ranges");
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inn.trim()) return;
    handleCalculate(inn);
  };

  const handleSuggestionClick = (suggestInn: string) => {
    setInn(suggestInn);
    handleCalculate(suggestInn);
  };

  // Экспорт CSV: пока не реализован server-side; собираем on-the-fly из result.
  const handleExportCsv = async () => {
    if (!result) return;
    // Строки блока Крылова — отдельным чанком: в главный бандл (бюджет 85 КБ) не тянем.
    const { krylovCsvRows } = await import("./utils/krylov");
    const rows = [
      ["Параметр", "Значение"],
      ["ИНН", result.company.inn],
      ["Полное наименование", result.company.fullName],
      ["Статус", result.company.status],
      ["ANYskin: рейтинг", result.anyskin.rating],
      ["ANYskin: итог", String(result.anyskin.totalScore)],
      ["Credit: F-score", String(result.credit.fScore)],
      ["Credit: риск", result.credit.riskCategory],
      ["Solvency: интерпретация", result.solvency.interpretation],
      ["Score: баллов", String(result.score.totalPoints) + " (" + result.score.scoreClass + ")"],
      ["Score: Sberbank", String(result.score.sberbankScore) + " (" + result.score.sberbankClass + ")"],
      ...krylovCsvRows(result),
    ];
    const csv = "﻿" + rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `anyskin_${result.company.inn}_${Date.now()}.csv`;
    link.click();
  };

  // Ссылка из письма-приглашения — отдельная полноэкранная страница вместо обычного
  // приложения/формы входа: сотрудник ещё не может войти, он задаёт себе пароль.
  if (inviteToken) {
    return <InviteSetPassword token={inviteToken} onDone={() => setInviteToken(null)} />;
  }

  return (
    <div className={`min-h-screen font-sans selection:bg-indigo-100 selection:text-indigo-900 ${currentUser ? "app-canvas" : "bg-slate-50"}`}>
      {notif.taskToast && (
        <div className={`app-floating fixed top-[calc(4rem+env(safe-area-inset-top))] md:top-4 left-1/2 -translate-x-1/2 z-(--z-toast) max-w-[calc(100vw-1.5rem)] ${TOAST_TONE[notif.toastTone]} text-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg inline-flex items-center gap-2`}>
          {notif.toastTone === "positive" ? "✅" : "🔔"} {notif.taskToast}
          <button onClick={() => { setView(notif.toastView); notif.setTaskToast(null); }} className="underline cursor-pointer">открыть</button>
        </div>
      )}
      <div className="flex min-h-screen">
        {currentUser && (
          <Sidebar
            currentUser={currentUser}
            view={view}
            onNavigate={setView}
            onLogout={handleLogout}
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            dark={dark}
            onToggleTheme={toggleTheme}
            taskCount={notif.myTaskCount}
            applications={notif.appsUnseen}
          />
        )}

        <PremiumModal
          open={!!premiumModal}
          message={premiumModal}
          onClose={() => setPremiumModal(null)}
          onMore={() => { setPremiumModal(null); setView("premium"); }}
        />

        {/* Контент — на «листе» поверх серой подложки (оболочка нового дизайна, md+). */}
        <div className={`app-main flex-1 min-w-0 flex flex-col ${currentUser ? "app-sheet bg-slate-50" : ""}`}>
          {currentUser ? (
            // Мобильная верхняя панель — гамбургер открывает боковое меню (drawer).
            <header className="md:hidden sticky top-0 z-(--z-rail) min-h-[calc(3.5rem+env(safe-area-inset-top))] flex items-center gap-3 px-4 bg-white border-b border-slate-100 pt-[env(safe-area-inset-top)]">
              {/* Бейдж на «бургере»: задачи и новые события по заявкам видны, не открывая меню (M-44). */}
              <button onClick={() => setMobileNavOpen(true)} className="relative w-11 h-11 shrink-0 -ml-2 flex items-center justify-center text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer" title="Меню"
                aria-label={notif.myTaskCount + notif.appsUnseen.count > 0 ? `Открыть меню, новых: ${notif.myTaskCount + notif.appsUnseen.count}` : "Открыть меню"}>
                <Menu className="w-5 h-5" />
                {notif.myTaskCount + notif.appsUnseen.count > 0 && (
                  <span className={`absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold inline-flex items-center justify-center ${notif.appsUnseen.attention > 0 || notif.myTaskCount > 0 ? "bg-rose-500" : "bg-indigo-500"}`}>
                    {notif.myTaskCount + notif.appsUnseen.count}
                  </span>
                )}
              </button>
              <button onClick={() => setView("broker")} className="flex items-center gap-2 min-w-0 min-h-11 cursor-pointer" aria-label="На главную — к скорингу">
                <LogoWordmark className="h-[18px] min-[380px]:h-5" />
              </button>
              <button onClick={() => window.dispatchEvent(new CustomEvent("anyskin:open-help", { detail: {} }))}
                className="ml-auto w-11 h-11 shrink-0 flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-lg cursor-pointer" title="Справка по этому экрану" aria-label="Открыть справку">
                <HelpCircle className="w-5 h-5" />
              </button>
              <button onClick={toggleTheme} className="hidden min-[380px]:flex w-11 h-11 shrink-0 items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 rounded-lg cursor-pointer" title={dark ? "Светлая тема" : "Тёмная тема"} aria-label={dark ? "Светлая тема" : "Тёмная тема"}>
                {dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button onClick={() => setView("cabinet")} className="w-11 h-11 shrink-0 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm cursor-pointer" title="Личный кабинет" aria-label="Личный кабинет">
                {(currentUser.username || "?").trim().charAt(0).toUpperCase()}
              </button>
            </header>
          ) : (
            // Шапка экрана входа (боковая панель не показывается до авторизации).
            <header className="sticky top-0 z-(--z-rail) bg-white border-b border-slate-100">
              <div className="max-w-7xl mx-auto px-5 h-16 flex items-center gap-3">
                <div>
                  <h1 className="m-0"><LogoWordmark className="h-7 md:h-8" /></h1>
                  <p className="text-[10px] md:text-xs text-slate-450 font-medium">Многопрофильный финансовый экспресс-анализ контрагентов</p>
                </div>
                <button
                  onClick={() => setView("about")}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 px-3 py-1.5 rounded-xl cursor-pointer"
                >
                  <Sparkles className="w-3.5 h-3.5" /> О проекте
                </button>
              </div>
            </header>
          )}

          {/* Онлайн-справка (эпик #255, H2): плавающая «?» + панель по текущему экрану. */}
          {currentUser && <HelpDrawer view={view} onOpenCenter={() => setView("help")} />}
          {/* Новая версия после выкатки — плашка «Обновить» (#444, PWA-кэш). */}
          <UpdateBanner />

      {<Suspense fallback={<div className="flex items-center justify-center py-32 text-slate-400 text-sm animate-pulse">Загрузка раздела…</div>}>{view === "about" ? (
        /* Промо-страница проекта — доступна всем (в т.ч. до авторизации). */
        <ProjectPromo onBack={() => setView("main")} onStart={() => setView("main")} />
      ) : view === "premium" || (["builder", "reports", "experience", "clients"].includes(view) && !isPremium) ? (
        /* Промо-премиум: для view==="premium" и при попытке зайти в премиум-раздел без подписки. */
        <PremiumPromo onBack={() => setView("main")} />
      ) : view === "agent" && currentUser ? (
        <AiAgent onBack={() => setView("main")} initialQuestion={agentPrefill} initialResult={result}
          seedContext={agentContext?.context} seedProduct={agentContext?.product}
          onConsumedInitial={() => setAgentPrefill(null)} />
      ) : view === "broker" && currentUser ? (
        <BrokerAnalytics onBack={() => setView("main")} currentUser={currentUser}
          repeat={brokerRepeat} onRepeatTaken={() => setBrokerRepeat(null)}
          prefillInn={brokerInn} onPrefillTaken={() => setBrokerInn(null)} onAskAssistant={askAssistant} />
      ) : view === "clients" && (currentUser?.is_premium || currentUser?.is_admin) ? (
        /* Клиент — корневая сущность: список клиентов, внутри — расчёт, отчётность, опыт. */
        <ClientsTab onBack={() => setView("main")}
          onAnalyze={(clientInn) => { setInn(clientInn); setView("main"); handleCalculate(clientInn); }}
          onOpenBroker={(s, inn) => { setBrokerRepeat(s || null); setBrokerInn(inn || null); setView("broker"); }} />
      ) : view === "platform" && currentUser ? (
        /* Кабинет оператора площадки (#591): свои банки и их условия. Доступ даёт роль,
           которую назначает администратор, — сам экран лишь показывает, что пришло. */
        <PlatformCabinet onBack={() => setView("main")} />
      ) : view === "applications" && currentUser ? (
        <ApplicationsTab onBack={() => setView("main")} initialRef={notif.appRef} onSeen={notif.pollApps} />
      ) : view === "experience" && (currentUser?.is_premium || currentUser?.is_admin) ? (
        <ClientExperience onBack={() => setView("main")} />
      ) : view === "reports" && (currentUser?.is_premium || currentUser?.is_admin) ? (
        <QuarterlyReports onBack={() => setView("main")} />
      ) : view === "builder" && (currentUser?.is_premium || currentUser?.is_admin) ? (
        <MethodologyBuilder onBack={() => setView("main")} />
      ) : view === "admin" && currentUser?.is_admin ? (
        /* ── Отдельный режим админки ─────────────────────────────────────────
           Никаких полей расчёта / suggestions / других вкладок. Только то,
           что нужно администратору. */
        <FullPageSection title="Администрирование" description="Управление учётными записями пользователей." onBack={() => setView("main")}>
          <AdminTab />
        </FullPageSection>
      ) : view === "admindocs" && currentUser?.is_admin ? (
        /* Встроенная документация по функционалу суперадмина. */
        <AdminDocs onBack={() => setView("main")} />
      ) : view === "help_admin" && currentUser?.is_admin ? (
        /* Редактор онлайн-справки (эпик #255, H3): статьи help_articles. */
        <HelpAdmin onBack={() => setView("main")} />
      ) : view === "help" && currentUser ? (
        /* Полноэкранный справочный центр (эпик #255, H5). */
        <HelpCenter onBack={() => setView("main")} onNavigate={(v) => setView(v as typeof view)} />
      ) : view === "banks" && (currentUser?.is_admin || currentUser?.packages?.includes("bank_analytics")) ? (
        <BankRegistry onBack={() => setView("main")} />
      ) : view === "credit" && (currentUser?.is_admin || currentUser?.packages?.includes("bank_analytics")) ? (
        <CreditProducts onBack={() => setView("main")} />
      ) : view === "product_compare" && (currentUser?.is_admin || currentUser?.packages?.includes("bank_analytics")) ? (
        <ProductCompare onBack={() => setView("main")} />
      ) : view === "bankmethods" && currentUser?.is_admin ? (
        <BankMethods onBack={() => setView("main")} />
      ) : view === "tenders" && (currentUser?.is_admin || currentUser?.packages?.includes("tenders")) ? (
        /* Тендераналитика — отдельный суб-продукт (своя БД, свой домен,
           собственный репозиторий), больше не встроена в ANYskin как iframe.
           Кросс-доменный SSO (Фаза 6 плана выноса) — ядро минтит одноразовый
           60-секундный тикет (EdDSA, app/routers/sso.py), tender-analytics
           проверяет подпись по тому же JWKS и обменивает на свою сессию
           (POST /api/auth/sso-exchange на его стороне). Токен передаётся во
           ФРАГМЕНТЕ redirect-адреса, не в query — не уходит в access-логи. */
        <TenderAnalyticsLaunch onBack={() => setView("main")} />
      ) : view === "org" && currentUser ? (
        /* ── Кабинет организации: карточка / участники / API-ключ (Фаза 4) ── */
        <FullPageSection title="Организация" description="Карточка компании, пользователи и роли, интеграционный API-ключ." onBack={() => setView("main")}>
          <OrganizationTab currentUser={currentUser} />
        </FullPageSection>
      ) : view === "cabinet" && currentUser ? (
        /* ── Отдельный режим личного кабинета ────────────────────────────────
           Раньше Cabinet рисовался ниже корпоративной карточки только когда
           уже был выполнен расчёт. Теперь это полноэкранный режим — клик по
           user pill всегда ведёт на этот экран. */
        <FullPageSection title="Личный кабинет" description="Учётные данные, статус подтверждения email и ваша статистика расчётов." onBack={() => setView("main")}>
          {/* Кабинет площадки (#591): вход виден только оператору и владельцу площадки —
              роль назначает администратор, сам пользователь её себе выдать не может. */}
          {(currentUser.org_role === "platform_operator" || currentUser.org_role === "owner") && (
            <button onClick={() => setView("platform")}
              className="mb-5 w-full sm:w-auto inline-flex items-center gap-2 px-5 py-3 bg-white hover:bg-slate-50 border border-slate-200 rounded-2xl text-left cursor-pointer">
              <Building2 className="w-5 h-5 text-indigo-600 shrink-0" />
              <span>
                <span className="block text-sm font-bold font-display">Кабинет площадки</span>
                <span className="block t-micro text-slate-500">
                  Банки вашей площадки, их тарифы и условия
                </span>
              </span>
            </button>
          )}
          <CabinetTab currentUser={currentUser} history={history} onLogout={handleLogout}
            onProfileSaved={(u) => setCurrentUser({ ...currentUser, ...u })} />
        </FullPageSection>
      ) : (
      <main className={dashboard
        ? "max-w-7xl mx-auto px-5 pt-8 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
        : "max-w-2xl mx-auto px-5 pt-10 sm:pt-16 w-full flex flex-col gap-6"}>

        {/* «Поисковая» шапка главной — видна только при входе (нет результата). */}
        {!dashboard && (
          <div className="text-center space-y-3">
            <div className="w-14 h-14 mx-auto bg-gradient-to-br from-sky-500 to-indigo-600 rounded-2xl text-white grid place-items-center shadow-sm">
              <Search className="w-7 h-7" />
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold font-display text-slate-900 tracking-tight">
              {currentUser ? "Экспресс-анализ контрагента" : "СЭБ Anyskin"}
            </h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              {currentUser
                ? "Введите ИНН юрлица — оценим надёжность по 4 методикам и соберём отчёт."
                : "Войдите, чтобы запускать экспресс-анализ контрагентов."}
            </p>
            {!currentUser && (
              <button
                onClick={() => setView("about")}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 px-3.5 py-2 rounded-xl cursor-pointer"
              >
                <Sparkles className="w-3.5 h-3.5" /> Возможности проекта и для кого он
              </button>
            )}
          </div>
        )}

        {/* Left Interactive controls col (lg:col-span-4) */}
        <section className={dashboard ? "col-span-1 lg:col-span-4 space-y-6" : "space-y-6"}>
          {!currentUser ? (
            <AuthPanel auth={auth} />
          ) : (
            <AnalysisSearchForm
              inn={inn} onInnChange={setInn} innType={innType}
              desiredLimit={desiredLimit} onDesiredLimitChange={setDesiredLimit}
              isLoading={isLoading} reportFile={reportFile} onReportFileChange={setReportFile}
              reportInputRef={reportInputRef} onSubmit={handleSearchSubmit}
              onCalculateWithReport={handleCalculateWithReport}
              onSuggestionClick={handleSuggestionClick}
            />
          )}

          {/* Sidebar calculations Log history listings */}
          <HistorySidebar 
            history={history} 
            onSelectInn={(selectedInn) => {
              setInn(selectedInn);
              handleCalculate(selectedInn);
            }} 
            isLoadingHistory={isLoadingHistory}
          />
        </section>

        {/* Right Dashboard Analytics Frame (lg:col-span-8) — только при результате/загрузке/ошибке */}
        {dashboard && (
        <section ref={resultsRef} className="col-span-1 lg:col-span-8 space-y-6 min-w-0 scroll-mt-24">
          
          {/* Load indicator */}
          {isLoading && (
            <div className="bg-white p-12 rounded-2xl border border-slate-100 shadow-sm text-center flex flex-col items-center justify-center space-y-4">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                <Activity className="w-5 h-5 text-indigo-600 absolute top-3.5 left-3.5 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h4 className="font-bold text-slate-800 font-display">
                  {ocrPending ? "Распознаём скан через OCR…" : "Синхронизация с открытыми федеральными ресурсами..."}
                </h4>
                <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                  {ocrPending
                    ? "Извлекаем строки баланса из скана/фото — распознавание может занять до минуты, затем считаем методики по вашей отчётности."
                    : `Инициируем извлечение бухгалтерской отчетности, сверяем контрактный реестр и арбитражные споры контрагента ИНН ${inn}...`}
                </p>
              </div>
            </div>
          )}

          {/* Exception panel */}
          {error && !isLoading && (
            <div className="bg-rose-50 border border-rose-100 p-6 rounded-2xl flex items-start gap-4">
              <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1.5">
                <h4 className="font-bold text-rose-950 font-display">Возникла ошибка при обработке данных ИНН {inn}</h4>
                <p className="text-xs text-rose-800 leading-relaxed">{error}</p>
                <div className="pt-2 flex gap-3 text-xs">
                  <button 
                    onClick={() => handleCalculate(inn)}
                    className="bg-rose-100 hover:bg-rose-200 text-rose-900 px-3.5 py-1.5 rounded-lg font-bold transition-colors cursor-pointer"
                  >
                    Повторить расчет
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Complete computed widgets Dashboard */}
          {!isLoading && !error && result && (
            <div className="space-y-6">

              {result.reportBased && (
                <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 text-xs rounded-xl p-3 flex items-start gap-2">
                  <FileSpreadsheet className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>Расчёт выполнен по <b>загруженной вами отчётности</b> ({result.reportLines} строк баланса). Реквизиты, госконтракты и суды — из открытых источников.</span>
                </div>
              )}

              {result.projected && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-xl p-3 flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2"><TrendingUp className="w-4 h-4 shrink-0" />
                    <b>Проекция по рекомендованному балансу</b> — показатели при достижении целевых значений. В историю не сохраняется.</span>
                  <button onClick={exitProjection}
                    className="shrink-0 inline-flex items-center gap-1 bg-white/70 hover:bg-white border border-amber-200 text-amber-900 font-bold px-2.5 py-1 rounded-lg cursor-pointer">
                    <ChevronLeft className="w-3.5 h-3.5" /> К фактическим
                  </button>
                </div>
              )}

              <CompanyProfileCard
                result={result} isIp={isIp} sb={sb}
                onShowSbExplanation={() => setShowSbExplanation(true)}
                onSelectInfographic={setSelectedInfographic}
              />

              {/* Оценка не выполнялась: у предпринимателя нет бухгалтерской отчётности,
                  и расчёт возможен только по данным, предоставленным клиентом. Показываем
                  требование и способы его выполнить — вместо нулей и рейтинга «D». */}
              {result.scoring?.calculated === false ? (
                <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5 space-y-3">
                  <div className="flex items-center gap-2">
                    <FileWarning className="w-5 h-5 text-amber-600" />
                    <h4 className="font-bold font-display text-sm text-slate-900">Оценка пока не выполнена</h4>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed max-w-2xl">
                    {result.scoring?.message}
                  </p>
                  <ul className="text-xs text-slate-700 space-y-1 list-disc pl-5">
                    {(result.scoring?.howTo || []).map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                  <p className="t-meta text-slate-400">
                    Реквизиты, госконтракты, судебная история и проверки по реестрам показаны выше —
                    они собираются независимо от отчётности.
                  </p>
                </div>
              ) : (
              <ResultTabs
                result={result} activeTab={activeTab} setActiveTab={setActiveTab}
                activeCustomId={activeCustomId} setActiveCustomId={setActiveCustomId}
                isPremium={isPremium} setPremiumModal={setPremiumModal}
                reportBusy={reportBusy} onFullReport={handleFullReport}
                onAskAssistant={askAssistant} onOpenBankPicker={() => setShowBankPicker(true)}
                isLoading={isLoading} onRecalcTarget={handleRecalcTarget}
                onRecalcKrylovTarget={handleRecalcKrylovTarget}
                onExportCsv={handleExportCsv}
              />
              )}

            </div>
          )}

        </section>
        )}

      </main>
      )}</Suspense>}
        </div>
      </div>

      <Suspense fallback={null}>
        {selectedInfographic && result && (
          <InfographicModal
            kind={selectedInfographic}
            result={result}
            onClose={() => setSelectedInfographic(null)}
          />
        )}

        {showSbExplanation && result && (
          <SbExplanationModal
            result={result}
            onClose={() => setShowSbExplanation(false)}
          />
        )}

        {showBankPicker && (
          <BankMethodologyPicker
            onClose={() => setShowBankPicker(false)}
            onSaved={(keys) => {
              setShowBankPicker(false);
              // Перезапускаем анализ по текущему ИНН, чтобы подтянуть bankResults.
              const cur = result?.company?.inn || inn;
              if (cur) handleCalculate(cur);
              else if (keys.length === 0) setActiveTab("compare");
            }}
          />
        )}

        {/* ИИ-ассистент по «Подробнее» — плавающее окно, как «Диалог по заявке» в
            Брокере: текущий экран (вкладка, подбор) не теряется. Портал — чтобы
            fixed-позиционирование не ломалось вложенными transform/filter предков. */}
        {agentFloatingOpen && currentUser && createPortal(
          <div ref={agentPanelRef}
            className={`fixed z-(--z-fab) flex flex-col bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden ${agentBox ? "" : "bottom-5 right-5 w-[min(420px,calc(100vw-1.5rem))] h-[min(640px,calc(100vh-6rem))]"} ${agentDragging || agentResizing ? "select-none" : ""}`}
            style={agentBox ? { left: agentBox.left, top: agentBox.top, width: agentBox.width, height: agentBox.height } : undefined}>
            <div onMouseDown={startAgentDrag} title="Перетащить окно"
              className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-br from-sky-500 to-indigo-600 text-white shrink-0 cursor-move select-none">
              <div className="inline-flex items-center gap-2 text-sm font-bold min-w-0">
                <Bot className="w-4 h-4 shrink-0" /> <span className="truncate">AI-ассистент</span>
              </div>
              <button onClick={() => setAgentFloatingOpen(false)} title="Свернуть" aria-label="Свернуть"
                onMouseDown={(e) => e.stopPropagation()}
                className="p-1.5 rounded-lg hover:bg-white/20 cursor-pointer shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <Suspense fallback={<div className="p-4 text-xs text-slate-400">Загрузка…</div>}>
                <AiAgent floating onBack={() => { setAgentFloatingOpen(false); setAgentContext(null); }}
                  initialQuestion={agentPrefill} initialResult={result}
                  seedContext={agentContext?.context} seedProduct={agentContext?.product}
                  onConsumedInitial={() => setAgentPrefill(null)} />
              </Suspense>
            </div>
            {/* Ресайз со всех сторон: края n/s/w/e (левый и верхний двигают позицию) + углы */}
            <div onMouseDown={(e) => startAgentResize(e, "n")} title="Верхний край" className="absolute top-0 left-4 right-4 h-2 cursor-ns-resize hover:bg-indigo-300/40" />
            <div onMouseDown={(e) => startAgentResize(e, "s")} title="Нижний край" className="absolute bottom-0 left-4 right-4 h-2 cursor-ns-resize hover:bg-indigo-300/40" />
            <div onMouseDown={(e) => startAgentResize(e, "w")} title="Левый край" className="absolute left-0 top-4 bottom-4 w-2 cursor-ew-resize hover:bg-indigo-300/40" />
            <div onMouseDown={(e) => startAgentResize(e, "e")} title="Правый край" className="absolute right-0 top-4 bottom-4 w-2 cursor-ew-resize hover:bg-indigo-300/40" />
            <div onMouseDown={(e) => startAgentResize(e, "sw")} title="Угол" className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize z-10" />
            <div onMouseDown={(e) => startAgentResize(e, "se")} title="Растянуть окно" className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-10"
              style={{ background: "linear-gradient(135deg, transparent 0 45%, rgb(129 140 248) 45% 55%, transparent 55% 66%, rgb(129 140 248) 66% 76%, transparent 76%)" }} />
          </div>,
          document.body
        )}
      </Suspense>
    </div>
  );
}
